import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../firebase';

const ACTIVITY_COLLECTION = 'auditLogs';
const FULL_ACTIVITY_ROLES = new Set(['owner', 'admin']);
const BUSINESS_ACTIVITY_ROLES = new Set(['manager', 'staff']);
const BUSINESS_EVENT_PREFIXES = [
  'supplier.',
  'product.',
  'configuration.',
  'category.',
];

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeSearchValue = (value) => normalizeString(value).toLowerCase();

export const canViewActivityEvent = (role, eventType) => {
  const normalizedRole = normalizeSearchValue(role);
  const normalizedEventType = normalizeSearchValue(eventType);

  if (FULL_ACTIVITY_ROLES.has(normalizedRole)) {
    return true;
  }

  if (!BUSINESS_ACTIVITY_ROLES.has(normalizedRole)) {
    return false;
  }

  return BUSINESS_EVENT_PREFIXES.some((prefix) =>
    normalizedEventType.startsWith(prefix)
  );
};

const normalizeTimestamp = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const normalizeActor = (actor = {}) => ({
  uid: normalizeString(actor.uid),
  email: normalizeString(actor.email).toLowerCase(),
  name: normalizeString(actor.name || actor.displayName),
  displayName: normalizeString(actor.displayName || actor.name),
  role: normalizeString(actor.role),
  source: normalizeString(actor.source),
});

const normalizeActivityLog = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};
  const occurredAtDate = normalizeTimestamp(data.occurredAt);

  return {
    id: docSnapshot?.id || data.id,
    ...data,
    eventType: normalizeString(data.eventType),
    domain: normalizeString(data.domain),
    action: normalizeString(data.action),
    entityType: normalizeString(data.entityType),
    entityId: normalizeString(data.entityId),
    entityLabel: normalizeString(data.entityLabel),
    actor: normalizeActor(data.actor),
    summary: normalizeString(data.summary),
    severity: normalizeString(data.severity).toLowerCase() || 'info',
    activityVisible: data.activityVisible === true,
    changedFields: Array.isArray(data.changedFields) ? data.changedFields : [],
    changes: data.changes && typeof data.changes === 'object' ? data.changes : {},
    metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata : {},
    occurredAt: data.occurredAt ?? null,
    occurredAtDate,
  };
};

const getActivityDomainKey = (activityLog) => {
  const domain = normalizeSearchValue(activityLog?.domain);
  const eventPrefix = normalizeSearchValue(activityLog?.eventType).split('.')[0];
  const entityType = normalizeSearchValue(activityLog?.entityType);

  if (domain === 'users' || eventPrefix === 'user' || entityType === 'user') {
    return 'user';
  }

  if (
    domain === 'suppliers' ||
    eventPrefix === 'supplier' ||
    entityType.startsWith('supplier')
  ) {
    return 'supplier';
  }

  if (
    domain === 'products' ||
    eventPrefix === 'configuration' ||
    eventPrefix === 'product' ||
    [
      'configuration',
      'product',
      'productcomponent',
      'productvariant',
      'productsupplier',
      'pricehistory',
    ].includes(entityType)
  ) {
    return 'product';
  }

  if (domain === 'categories' || eventPrefix === 'category' || entityType === 'category') {
    return 'category';
  }

  if (domain === 'system' || eventPrefix === 'system' || entityType === 'system') {
    return 'system';
  }

  return '';
};

const getDateRangeStart = (dateRange) => {
  const now = Date.now();

  if (dateRange === '24h') {
    return new Date(now - 24 * 60 * 60 * 1000);
  }

  if (dateRange === '7d') {
    return new Date(now - 7 * 24 * 60 * 60 * 1000);
  }

  if (dateRange === '30d') {
    return new Date(now - 30 * 24 * 60 * 60 * 1000);
  }

  return null;
};

export const getActivityLogs = async (role) => {
  const isFullActivityRole = FULL_ACTIVITY_ROLES.has(normalizeSearchValue(role));
  const activityQuery = query(
    collection(db, ACTIVITY_COLLECTION),
    ...(isFullActivityRole ? [] : [where('activityVisible', '==', true)]),
    orderBy('occurredAt', 'desc')
  );
  const querySnapshot = await getDocs(activityQuery);

  return querySnapshot.docs
    .map((docSnapshot) => normalizeActivityLog(docSnapshot))
    .filter((activityLog) => canViewActivityEvent(role, activityLog.eventType));
};

export const searchActivityLogs = (activityLogs = [], searchTerm = '') => {
  const queryText = normalizeSearchValue(searchTerm);

  if (!queryText) {
    return activityLogs;
  }

  return activityLogs.filter((activityLog) =>
    [
      activityLog?.actor?.name,
      activityLog?.actor?.displayName,
      activityLog?.actor?.email,
      activityLog?.entityLabel,
      activityLog?.summary,
    ].some((value) => normalizeSearchValue(value).includes(queryText))
  );
};

export const filterActivityLogs = (
  activityLogs = [],
  { eventType = 'all', dateRange = 'all', severity = 'all' } = {}
) => {
  const dateRangeStart = getDateRangeStart(dateRange);
  const normalizedSeverity = normalizeSearchValue(severity);

  return activityLogs.filter((activityLog) => {
    if (eventType !== 'all' && getActivityDomainKey(activityLog) !== eventType) {
      return false;
    }

    if (dateRangeStart) {
      const occurredAtDate = normalizeTimestamp(activityLog?.occurredAtDate);

      if (!occurredAtDate || occurredAtDate < dateRangeStart) {
        return false;
      }
    }

    if (
      normalizedSeverity &&
      normalizedSeverity !== 'all' &&
      normalizeSearchValue(activityLog?.severity) !== normalizedSeverity
    ) {
      return false;
    }

    return true;
  });
};

export const formatActivityDateTime = (value) => {
  const date = normalizeTimestamp(value);

  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};
